import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './style/TestReview.css';
import { InlineMath, BlockMath } from 'react-katex';
import 'katex/dist/katex.min.css';
import TableBlock from '../components/TableBlock';
import notFlagged from '../images/notFlagged.png';
import flagged from '../images/flagged.png';
import ToolsPanel from '../components/ToolsPanel';
import { authFetch } from '../authFetch';

function TestReview() {
  const [pageLoading, setPageLoading] = useState(true);
  const [questions, setQuestions] = useState(null);
  const [attemptedQuestions, setAttemptedQuestions] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(1);
  const [toolsCollapsed, setToolsCollapsed] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);
  const [desmosReady, setDesmosReady] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const testId = location.state?.testId;
  const attemptNumber = location.state?.attemptNumber || 1;
  const testName = location.state?.testName || "";

  const getAttemptForQuestion = (questionNum) => {
    if (!attemptedQuestions) return null;
    return attemptedQuestions.find(a => a.question_number === questionNum) || null;
  };

  const getAnswerClasses = (questionNum) => {
    const classes = ['', '', '', '', ''];
    const attempt = getAttemptForQuestion(questionNum);
    const question = questions?.find(q => q.question_number === questionNum);
    if (!attempt || attempt.selected_answer === null) {
      if (question?.question_type === 'FRQ') {classes[4] = 'incorrect';} 
      else {
        const correctIdx = parseInt(question?.correct_answer);
        if (!isNaN(correctIdx)) classes[correctIdx] = 'correct';
      }
      return classes;
    }
    if (question?.question_type === 'FRQ') {classes[4] = attempt.correct ? 'correct' : 'incorrect';} 
    else {
      const idx = parseInt(attempt.selected_answer);
      if (attempt.correct) {classes[idx] = 'correct';} 
      else {
        classes[idx] = 'incorrect';
        const correctIdx = parseInt(question?.correct_answer);
        if (!isNaN(correctIdx)) classes[correctIdx] = 'correct';
      }
    }
    return classes;
  };

  const initialLoad = async () => {
    try {
      const response = await authFetch(`${process.env.REACT_APP_API_BASE}/testReview/initialLoad/${testId}/${attemptNumber}`, {
        method: 'GET',
        headers: { 
          'Content-Type': 'application/json' 
        },
      });
      const data = await response.json();
      setQuestions(data.questions);
      setAttemptedQuestions(data.attemptedQuestions);
    } 
    catch (error) {
      console.error('Error:', error);
    }
  };

  const handleQuestionButtonClick = (questionNum) => {
    setCurrentQuestion(questionNum);
    setShowExplanation(false);
  };

  const handleNextClick = () => {
    if (currentQuestion < 22) {
      const nextQuestion = currentQuestion + 1;
      setCurrentQuestion(nextQuestion);
      setShowExplanation(false);
    }
  };

  const handlePreviousClick = () => {
    if (currentQuestion > 1) {
      const prevQuestion = currentQuestion - 1;
      setCurrentQuestion(prevQuestion);
      setShowExplanation(false);
    }
  };

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        e.stopPropagation();
        handleNextClick();
      }
      else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        e.stopPropagation();
        handlePreviousClick();
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentQuestion]);

  useEffect(() => {
    if (!location.state) {
      navigate('/tests', { replace: true });
      return;
    }
    initialLoad();
  }, []);

  useEffect(() => {
    setPageLoading(!(desmosReady && questions && attemptedQuestions));
  }, [desmosReady, questions, attemptedQuestions]);

  return (
    <div className='test-review-container'>
      <div className='mode-header'>
        <p>#{testName}</p>
      </div>
      <div className={`main-layout ${toolsCollapsed ? 'tools-collapsed' : ''}`} style={pageLoading ? { visibility: 'hidden', position: 'absolute' } : undefined}>
        <ToolsPanel onReady={() => setDesmosReady(true)} resetKey={currentQuestion} onCollapse={setToolsCollapsed} />
        <div className='question-content'>
          <div className='question-box'>
            <div className='question-info'>
              <p>{`${currentQuestion}`}</p>
              {getAttemptForQuestion(currentQuestion)?.flagged ? (
                <img src={flagged} alt="Flagged" />
              ) : (
                <img src={notFlagged} alt="Not Flagged" />
              )}
            </div>
            <div className='question-body'>
              <div className='question-text'>
                <p>
                  {questions ? (
                    questions.find(q => q.question_number === currentQuestion)?.content.map((block, i) => {
                      switch (block.type) {
                        case "text":
                          return <span key={i}>{block.content} </span>;
                        case "inline_math":
                          return <InlineMath key={i} math={block.content} />;
                        case "block_math":
                          return <BlockMath key={i} math={block.content} />;
                        case "image":
                          return <img key={i} src={`${process.env.REACT_APP_API_BASE}${block.src}`} alt={block.alt} />;
                        case "table":
                          return <TableBlock key={i} headers={block.headers} rows={block.rows} />;
                        case "line_break":
                          return <br key={i} />;
                        default:
                          return null;
                      }
                    })
                  ) : null}
                </p>
              </div>
              <div className='answer-options'>
                {showExplanation && questions?.find(q => q.question_number === currentQuestion)?.explanation ? (
                  <div className='explanation-content'>
                    {questions.find(q => q.question_number === currentQuestion).explanation.map((block, i) => {
                      switch (block.type) {
                        case "text":
                          return <span key={i}>{block.content}</span>;
                        case "inline_math":
                          return <InlineMath key={i} math={block.content} />;
                        case "block_math":
                          return <BlockMath key={i} math={block.content} />;
                        case "image":
                          return <img key={i} src={`${process.env.REACT_APP_API_BASE}${block.src}`} alt={block.alt} />;
                        case "table":
                          return <TableBlock key={i} headers={block.headers} rows={block.rows} />;
                        case "line_break":
                          return <br key={i} />;
                        default:
                          return null;
                      }
                    })}
                  </div>
                ) : questions && questions.find(q => q.question_number === currentQuestion)?.question_type === 'FRQ' ? (
                  <>
                    <input
                      type="text"
                      value={getAttemptForQuestion(currentQuestion)?.selected_answer || ''}
                      readOnly
                      className={getAnswerClasses(currentQuestion)[4]}
                    />
                    {questions.find(q => q.question_number === currentQuestion)?.correct_answer != null && <p className='correct-answer'>Correct Answer: {questions.find(q => q.question_number === currentQuestion).correct_answer}</p>}
                  </>
                ) : (
                  <>
                    {['A', 'B', 'C', 'D'].map((label, index) => (
                      <button key={index} className={getAnswerClasses(currentQuestion)[index]} >
                        <p>{`${label})`}</p>
                        <p>
                          {questions && questions.find(q => q.question_number === currentQuestion)?.answer_choices && questions.find(q => q.question_number === currentQuestion).answer_choices[index] ? (
                            questions.find(q => q.question_number === currentQuestion).answer_choices[index].map((block, i) => {
                              switch (block.type) {
                                case "text":
                                  return <span key={i}>{block.content}</span>;
                                case "inline_math":
                                  return <InlineMath key={i} math={block.content} />;
                                case "block_math":
                                  return <BlockMath key={i} math={block.content} />;
                                case "image":
                                  return <img key={i} src={`${process.env.REACT_APP_API_BASE}${block.src}`} alt={block.alt} />;
                                case "table":
                                  return <TableBlock key={i} headers={block.headers} rows={block.rows} />;
                                default:
                                  return null;
                              }
                            })
                          ) : ''}
                        </p>
                      </button>
                    ))}
                  </>
                )}
              </div>
            </div>
          </div>
          <div className='question-numbers'>
            {Array.from({ length: 22 }, (_, i) => i + 1).map((num) => {
              const classes = [];
              if (num === currentQuestion) classes.push('active');
              const attempt = getAttemptForQuestion(num);
              if (attempt) {
                classes.push(attempt.correct ? 'correct' : 'incorrect');
              } else {
                classes.push('incorrect');
              }
              return (
                <button
                  key={num}
                  className={classes.join(' ')}
                  onClick={() => handleQuestionButtonClick(num)}
                >
                  {num}
                </button>
              );
            })}
          </div>
          <div className='action-controls'>
            <div className='explanation-buttons'>
              {showExplanation ? <button id='answers' onClick={() => setShowExplanation(false)}>Show Answers</button> : <button id='explanation' onClick={() => setShowExplanation(true)}>Explanation</button>}
            </div>
            <div className='navigation-buttons'>
              <button id='previous' onClick={handlePreviousClick}>Previous</button>
              <button id='next' onClick={handleNextClick}>Next</button>
            </div>
          </div>
        </div>
      </div>
      {pageLoading && <div className='main-layout-loading'>
        <div className='three-circle-loader'>
          <span />
          <span />
          <span />
        </div>
      </div>}
    </div>
  );
}

export default TestReview;
